import React from "react";

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "N/A";
const fmtDt = (d) => d ? new Date(d).toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "N/A";

const WorkOrderPrintLayout = ({
  workOrderDetails,
  comments = [],
  selectedAssignee,
  selectedPriority,
  workOrderTypes = [],
  workOrderSubtypes = [],
  workOrderLocations = [],
  selectedTypeId,
  selectedSubtypeId,
  selectedLocationId,
  member,
  attachedFiles = [],
  apiBaseUrl = "",
  statusHistory = [],
}) => {
  if (!workOrderDetails) return null;

  const w = workOrderDetails;
  const type = workOrderTypes.find((t) => t.id === selectedTypeId)?.type || "N/A";
  const subtype = workOrderSubtypes.find((s) => s.id === selectedSubtypeId)?.subtype_name || "N/A";
  const location = workOrderLocations.find((l) => l.id === selectedLocationId)?.location_name || "N/A";

  const imageFiles = attachedFiles.filter((f) => f.file_type?.startsWith("image/"));
  const otherFiles = attachedFiles.filter((f) => !f.file_type?.startsWith("image/"));

  const getImageUrl = (file) =>
    `${apiBaseUrl}/workorderimages/${encodeURIComponent(file.file_url.split(/(\\|\/)/g).pop())}`;

  const statusColor = w.status === "Completed" || w.status === "Closed"
    ? "#22c55e"
    : w.status === "In Progress"
    ? "#f59e0b"
    : w.status === "Open"
    ? "#ef4444"
    : "#6b7280";

  return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif", color: "#1a202c", margin: 0, padding: 0, fontSize: "11pt" }}>

      {/* HEADER */}
      <div style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #2f63ad 100%)", color: "#fff", padding: "24px 32px 20px", borderRadius: "0 0 8px 8px", marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: "9pt", letterSpacing: 2, textTransform: "uppercase", opacity: 0.75, marginBottom: 4 }}>Heritage Springs Community HOA</div>
            <div style={{ fontSize: "22pt", fontWeight: 700, marginBottom: 4 }}>Work Order Report</div>
            <div style={{ fontSize: "13pt", opacity: 0.9 }}>Work Order #{w.order_id}</div>
          </div>
          <div style={{ textAlign: "right", fontSize: "9pt", opacity: 0.85 }}>
            <div>Printed: {fmtDt(new Date())}</div>
            <div style={{ marginTop: 4 }}>My HOA Management System</div>
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <span style={{ background: statusColor, color: "#fff", padding: "4px 14px", borderRadius: 20, fontWeight: 700, fontSize: "10pt" }}>
            {w.status || "N/A"}
          </span>
        </div>
      </div>

      <div style={{ padding: "0 32px 32px" }}>

        {/* WORK ORDER DETAILS */}
        <Section title="Work Order Details">
          <Grid2Col>
            <Field label="Order ID" value={`#${w.order_id}`} />
            <Field label="Status" value={w.status} />
            <Field label="Type" value={type} />
            <Field label="Subtype" value={subtype} />
            <Field label="Location" value={location} />
            <Field label="Priority" value={selectedPriority || "N/A"} />
            <Field label="Assigned To" value={selectedAssignee?.name || "Unassigned"} />
            <Field label="Created" value={fmtDt(w.created_at)} />
            {w.resolved_at && <Field label="Resolved" value={fmtDt(w.resolved_at)} />}
          </Grid2Col>
          {w.description && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 600, color: "#4a5568", marginBottom: 4, fontSize: "9pt", textTransform: "uppercase", letterSpacing: 1 }}>Description</div>
              <div style={{ background: "#f7f9fc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "10px 14px", lineHeight: 1.6 }}>{w.description}</div>
            </div>
          )}
        </Section>

        {/* MEMBER INFO */}
        {member && (
          <Section title="Member & Property">
            <Grid2Col>
              <Field label="Owner Name" value={`${member.FirstName || ""} ${member.LastName || ""}`.trim() || "N/A"} />
              <Field label="Member Code" value={member.ClubMemberCode || "N/A"} />
              <Field label="Address" value={member.FullAddress || member.address || "N/A"} />
              <Field label="Phone" value={member.PhoneNumber || member.phone || "N/A"} />
              <Field label="Email" value={member.EmailAddress || member.email || "N/A"} />
            </Grid2Col>
          </Section>
        )}

        {/* IMAGES */}
        {imageFiles.length > 0 && (
          <Section title="Attached Images">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {imageFiles.map((f, i) => (
                <div key={i} style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", maxWidth: 260 }}>
                  <img src={getImageUrl(f)} alt={f.file_name || "Attachment"}
                    style={{ width: "100%", display: "block", maxHeight: 190, objectFit: "cover" }}
                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  <div style={{ padding: "6px 10px", fontSize: "9pt", color: "#4a5568" }}>{f.file_name}</div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* OTHER ATTACHMENTS */}
        {otherFiles.length > 0 && (
          <Section title="Attachments">
            <table style={tableStyle}>
              <thead><tr style={thRowStyle}><Th>File Name</Th><Th>Type</Th></tr></thead>
              <tbody>
                {otherFiles.map((f, i) => (
                  <tr key={i} style={i % 2 === 0 ? {} : altRowStyle}>
                    <Td>{f.file_name}</Td><Td>{f.file_type || "N/A"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {/* COMMENTS */}
        <Section title={`Comments (${comments.length})`}>
          {comments.length === 0
            ? <div style={{ color: "#6b7280", fontStyle: "italic" }}>No comments recorded.</div>
            : comments.map((c, i) => (
              <div key={c.comment_id || i} style={{ borderLeft: "3px solid #2f63ad", marginBottom: 12, background: i % 2 === 0 ? "#f7f9fc" : "#fff", borderRadius: "0 6px 6px 0", padding: "8px 12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, color: "#2f63ad" }}>{c.user_name} {c.user_lastname}</span>
                  <span style={{ fontSize: "9pt", color: "#6b7280" }}>{fmtDt(c.sent_at)}</span>
                </div>
                <div style={{ color: "#1a202c", lineHeight: 1.5 }}>{c.comment_text}</div>
              </div>
            ))
          }
        </Section>

        {/* STATUS HISTORY */}
        {statusHistory.length > 0 && (
          <Section title="Status History">
            <table style={tableStyle}>
              <thead><tr style={thRowStyle}><Th>Date</Th><Th>From</Th><Th>To</Th><Th>Changed By</Th></tr></thead>
              <tbody>
                {statusHistory.map((h, i) => (
                  <tr key={i} style={i % 2 === 0 ? {} : altRowStyle}>
                    <Td>{fmtDt(h.changed_at)}</Td>
                    <Td>{h.old_status || "—"}</Td>
                    <Td>{h.new_status || "—"}</Td>
                    <Td>{h.changed_by_name || h.changed_by || "N/A"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {/* FOOTER */}
        <div style={{ marginTop: 32, borderTop: "2px solid #e2e8f0", paddingTop: 12, display: "flex", justifyContent: "space-between", fontSize: "9pt", color: "#6b7280" }}>
          <span>Heritage Springs Community HOA — My HOA Management System</span>
          <span>Printed: {fmtDt(new Date())}</span>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
      <div style={{ background: "#2f63ad", width: 4, height: 18, borderRadius: 2 }} />
      <div style={{ fontWeight: 700, fontSize: "11pt", color: "#1e3a5f", textTransform: "uppercase", letterSpacing: 0.5 }}>{title}</div>
    </div>
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px" }}>{children}</div>
  </div>
);

const Grid2Col = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>{children}</div>
);

const Field = ({ label, value }) => (
  <div style={{ paddingBottom: 4 }}>
    <div style={{ fontSize: "8.5pt", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 1 }}>{label}</div>
    <div style={{ fontWeight: 600, color: "#1a202c" }}>{value || "N/A"}</div>
  </div>
);

const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: "10pt" };
const thRowStyle = { background: "#f0f4fa" };
const altRowStyle = { background: "#f7f9fc" };
const Th = ({ children }) => (
  <th style={{ padding: "7px 10px", textAlign: "left", fontWeight: 700, color: "#1e3a5f", fontSize: "9pt", borderBottom: "2px solid #2f63ad" }}>{children}</th>
);
const Td = ({ children }) => (
  <td style={{ padding: "7px 10px", borderBottom: "1px solid #e2e8f0", color: "#1a202c" }}>{children}</td>
);

export default WorkOrderPrintLayout;
